import { produce } from 'immer';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../../../../../../../common/widgets/Button';
import { CheckboxGroup } from '../../../../../../../common/widgets/CheckboxGroup';
import Dropdown from '../../../../../../../common/widgets/Dropdown';
import Icons from '../../../../../../../common/widgets/Icons';
import Input from '../../../../../../../common/widgets/Input';
import PopoverDialog from '../../../../../../../common/widgets/PopoverDialog';
import TextArea from '../../../../../../../common/widgets/TextArea';
import { BarcodeTypes } from '../../../../../../benefit/enums';
import { ErrorState } from '../../../../../../benefit/models/Errors';
import { CodeSourceTypes, SoCodeTypes } from '../../../../../enums';
import { CodeModel } from '../../../../../models/codes';
import { resources } from '../../../../../resources/resources';
import { codesService } from '../../../../../services/codes';
import { ProductPreview } from '../../../Common/ProductPreview';
import { GeneratedCodesDialog } from '../../Codes/GeneratedDialog';
import { ManuallyAddedCodesDialog } from '../../Codes/ManuallyAddedDialog';
import { PreviewDialog } from '../../Codes/PreviewDialog';
import {
  CodeDialogTypes,
  CodeSources,
  formatCode,
  InvalidCodes,
  ValidCodes,
} from '../../Codes/utils';
import { CodesSourceSelector } from '../../CodesSourceSelector';
import { getBarcodeTypeOptions, ProductContext } from '../../constants';
import { ProductPreviewData } from '../../types';

export interface GiftCardOfferDetailsForm {
  defaultPrice?: number;
  discountedPrice?: number;
  codesSource?: CodeSources;
  codes: {
    generated: CodeModel[];
    manuallyAdded: { valid: CodeModel[]; invalid: CodeModel[] };
  };
  readonlyCodes?: string[];
  orderInfo: boolean;
  emails?: string;
  barcodeType?: { id: BarcodeTypes; text: string };
  codesExpirationDate?: Date;
}

interface GiftCardOfferDetailsProps {
  productId?: number;
  productName?: string;
  previewData?: ProductPreviewData;
  codeSourceType?: CodeSourceTypes;
  soCodeType?: SoCodeTypes;
  onlyAddCodes: boolean;
  isSaveButtonLoading?: boolean;
  errors: ErrorState;
  setErrors: (errors: ErrorState) => void;
  offerDetailsForm: GiftCardOfferDetailsForm;
  setOfferDetailsForm: (form: GiftCardOfferDetailsForm) => void;
  buttonFunctions?: {
    onBackClick: () => void;
    onContinueClick: () => void;
  };
  context: ProductContext;
}

export const GiftCardOfferDetails = (props: GiftCardOfferDetailsProps) => {
  const {
    productId,
    productName,
    previewData,
    codeSourceType,
    soCodeType,
    onlyAddCodes,
    isSaveButtonLoading,
    offerDetailsForm,
    setOfferDetailsForm,
    errors,
    setErrors,
    buttonFunctions,
    context,
  } = props;
  const { t: translate } = useTranslation();
  const [activeDialog, setActiveDialog] = useState<
    CodeDialogTypes | undefined
  >();
  const [existingCodesLoading, setExistingCodesLoading] =
    useState<boolean>(false);

  const clearErrors = (field: string) => {
    setErrors(
      produce(errors, d => {
        d[field] = [];
      }),
    );
  };

  return (
    <>
      {previewData && (
        <ProductPreview
          productTypes={previewData.productTypes}
          producyType={previewData.producyType}
          productStatuses={previewData.productStatuses}
          status={previewData.status}
          name={previewData.name}
          imageContent={previewData.imageContent}
          defaultPrice={offerDetailsForm.defaultPrice}
          discountedPrice={offerDetailsForm.discountedPrice}
          showPricePreview
        />
      )}
      <div className="m-panel">
        <h1>{translate(resources.products.add.offerDetails.title)}</h1>

        <div className="m-form">
          <div className="m-form-section">
            <div className="m-form-label">
              <h4>
                {translate(resources.products.add.offerDetails.productDiscount)}
                {'*'}
                <PopoverDialog
                  className="m-tooltip"
                  placement="bottom"
                  appendTo={document.body}
                  type="click"
                  content={translate(
                    resources.products.add.offerDetails.discountedPriceInfo,
                  )}
                >
                  <div className="m-label-tooltip">
                    <Icons.MInfoTooltip />
                  </div>
                </PopoverDialog>
              </h4>
            </div>
            <div className="m-form-input">
              <div className="row">
                <div className="col col-6">
                  <Input
                    type="number"
                    label={translate(
                      resources.products.add.offerDetails.defaultPrice,
                    )}
                    value={offerDetailsForm.defaultPrice}
                    onChange={e =>
                      setOfferDetailsForm(
                        produce(offerDetailsForm, d => {
                          d.defaultPrice = Number.parseFloat(e.value);
                        }),
                      )
                    }
                    mandatory
                    errors={errors.defaultPrice}
                    clearErrors={() => clearErrors('defaultPrice')}
                    disabled={onlyAddCodes}
                    controlClassName="mb-0"
                  />
                </div>
                <div className="col col-6">
                  <Input
                    type="number"
                    label={translate(
                      resources.products.add.offerDetails.discountedPrice,
                    )}
                    value={offerDetailsForm.discountedPrice}
                    onChange={e => {
                      setOfferDetailsForm(
                        produce(offerDetailsForm, d => {
                          d.discountedPrice = Number.parseFloat(e.value);
                        }),
                      );
                    }}
                    mandatory
                    errors={errors.discountedPrice}
                    clearErrors={() => clearErrors('discountedPrice')}
                    disabled={onlyAddCodes}
                    controlClassName="mb-0"
                  />
                </div>
              </div>
            </div>
          </div>

          <hr className="m-form-separator" />

          <div className="m-form-section">
            <div className="m-form-label">
              <h4>
                {translate(resources.codes.barcodeType.label)}
                {'*'}
              </h4>
            </div>
            <div className="m-form-input">
              <Dropdown
                value={
                  offerDetailsForm.barcodeType ?? {
                    id: null,
                    text: translate(resources.select),
                  }
                }
                onChange={e =>
                  setOfferDetailsForm(
                    produce(offerDetailsForm, d => {
                      d.barcodeType = e.value;
                    }),
                  )
                }
                data={getBarcodeTypeOptions(translate)}
                textField="text"
                dataItemKey="id"
                errors={errors.barcodeType}
                clearErrors={() => clearErrors('barcodeType')}
                mandatory
                disabled={onlyAddCodes}
              />
              <span className="m-form-info">
                {translate(resources.codes.barcodeType.info)}
              </span>
            </div>
          </div>

          {((!!codeSourceType &&
            !offerDetailsForm.codes.generated?.length &&
            !offerDetailsForm.codes.manuallyAdded.valid.length) ||
            context === ProductContext.Edit) && (
            <>
              <div className="m-form-section">
                <div className="m-form-label">
                  <h4>
                    {soCodeType === SoCodeTypes.Default
                      ? `${translate(resources.codes.defaultCodes)}*`
                      : `${translate(resources.codes.pinCodes)}*`}
                    <br />
                    {codeSourceType === CodeSourceTypes.ManuallyAdded
                      ? translate(resources.codes.manuallyAdded)
                      : translate(resources.codes.generated)}
                  </h4>
                </div>
                <div className="m-form-input">
                  <div className="m-form-buttons">
                    {!!codeSourceType &&
                      !offerDetailsForm.codes.generated?.length &&
                      !offerDetailsForm.codes.manuallyAdded.valid.length && (
                        <Button
                          className="btn btn-solid"
                          onClick={() => {
                            if (codeSourceType === CodeSourceTypes.Generated) {
                              setActiveDialog(CodeDialogTypes.Generate);
                            }
                            if (
                              codeSourceType === CodeSourceTypes.ManuallyAdded
                            ) {
                              setActiveDialog(CodeDialogTypes.ManuallyAdded);
                            }
                          }}
                          icon={<Icons.MButtonAdd />}
                        >
                          <span className="btn-text">
                            {translate(resources.codes.add.newCodes)}
                          </span>
                        </Button>
                      )}
                    {context === ProductContext.Edit && (
                      <Button
                        className="btn btn-outline"
                        onClick={async () => {
                          setExistingCodesLoading(true);
                          await codesService.downloadExistingCodes(
                            productId!,
                            translate(resources.codes.download.title, {
                              0: productName ?? '',
                            }),
                          );
                          setExistingCodesLoading(false);
                        }}
                        loading={existingCodesLoading}
                        icon={<Icons.MDownloadCodes />}
                      >
                        <span className="btn-text">
                          {translate(resources.codes.downloadExistingCodes)}
                        </span>
                      </Button>
                    )}
                  </div>
                </div>
              </div>
              <hr className="m-form-separator" />
            </>
          )}

          {!codeSourceType && (
            <>
              <div className="m-form-section">
                <div className="m-form-label">
                  <h4>
                    {translate(resources.codes.label)}
                    {'*'}
                  </h4>
                </div>
                <div className="m-form-input">
                  <CodesSourceSelector
                    codesSource={offerDetailsForm.codesSource}
                    onChange={codesSource => {
                      if (codesSource === CodeSources.Generated) {
                        setActiveDialog(CodeDialogTypes.Generate);
                      }
                      if (codesSource === CodeSources.ManuallyAdded) {
                        setActiveDialog(CodeDialogTypes.ManuallyAdded);
                      }
                      clearErrors('codeSource');
                    }}
                    errors={errors.codeSource}
                  />
                  <span className="m-form-info">
                    {translate(resources.codes.info)}
                  </span>
                </div>
              </div>
              {/* <hr className="m-form-separator" /> to be uncommented when codes expiration date is uncommented */}
            </>
          )}

          {!!offerDetailsForm.codes.generated?.length && (
            <div className="m-form-section">
              <div className="m-form-label">
                <h4>{`${translate(resources.codes.defaultCodes)}${translate(
                  resources.codes.generated,
                )}`}</h4>
              </div>
              <div className="m-form-input">
                <TextArea
                  value={offerDetailsForm.codes.generated
                    .map(formatCode)
                    .join('\n')}
                  readOnly
                />
                <div className="m-form-buttons">
                  <Button
                    className="btn btn-solid"
                    onClick={() => setActiveDialog(CodeDialogTypes.Generate)}
                    icon={<Icons.MButtonReplace />}
                  >
                    <span className="btn-text">
                      {translate(resources.codes.replace)}
                    </span>
                  </Button>
                </div>
              </div>
            </div>
          )}

          {(!!offerDetailsForm.codes.manuallyAdded.valid.length ||
            !!offerDetailsForm.codes.manuallyAdded.invalid.length) && (
            <div className="m-form-section">
              <div className="m-form-label">
                <h4>{`${translate(resources.codes.defaultCodes)}${translate(
                  resources.codes.manuallyAdded,
                )}`}</h4>
              </div>
              <div className="m-form-input">
                {!!offerDetailsForm.codes.manuallyAdded.valid.length && (
                  <>
                    <TextArea
                      label={`${translate(
                        resources.codes.defaultCodes,
                      )}${translate(resources.codes.manuallyAdded)}`}
                      value={offerDetailsForm.codes.manuallyAdded.valid
                        .map(formatCode)
                        .join('\n')}
                      rows={8}
                      readOnly
                    />
                    <ValidCodes
                      codes={offerDetailsForm.codes.manuallyAdded.valid}
                    />
                  </>
                )}
                {!!offerDetailsForm.codes.manuallyAdded.invalid.length && (
                  <div className="invalid-codes-alert">
                    <InvalidCodes
                      codes={offerDetailsForm.codes.manuallyAdded.invalid}
                    />
                  </div>
                )}
                <div className="replace-codes-file">
                  <Button
                    className="btn btn-solid"
                    onClick={() =>
                      setActiveDialog(CodeDialogTypes.ManuallyAdded)
                    }
                    icon={<Icons.MButtonReplaceFile />}
                  >
                    <span className="btn-text">
                      {translate(resources.codes.replace)}
                    </span>
                  </Button>
                  {!!offerDetailsForm.codes.manuallyAdded.invalid.length && (
                    <span>
                      {translate(resources.codes.manual.continueWithValidCodes)}
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* <div className="m-form-section">
            <div className="m-form-label">
              <h4>{translate(resources.codes.expirationDate)}</h4>
            </div>
            <div className="m-form-input">
              <div className="row">
                <div className="col col-6">
                  <DatePicker
                    value={offerDetailsForm.codesExpirationDate}
                    onChange={e =>
                      setOfferDetailsForm(
                        produce(offerDetailsForm, d => {
                          d.codesExpirationDate = new Date(e.target.value);
                        }),
                      )
                    }
                    errors={errors.codesExpirationDate}
                    clearErrors={() => clearErrors('codesExpirationDate')}
                    controlClassName="mb-0"
                  />
                </div>
              </div>
            </div>
          </div> */}

          <hr className="m-form-separator" />

          <div className="m-form-section">
            <div className="m-form-label">
              <h4>
                {translate(
                  resources.products.add.offerDetails.giftCard.orderInfo.label,
                )}
              </h4>
            </div>
            <div className="m-form-input m-form-margin-0">
              <CheckboxGroup
                checkboxes={[
                  {
                    label: translate(
                      resources.products.add.offerDetails.giftCard.orderInfo
                        .checkbox,
                    ),
                    isChecked: offerDetailsForm.orderInfo,
                    onChange: e =>
                      setOfferDetailsForm(
                        produce(offerDetailsForm, d => {
                          d.orderInfo = e.value;
                        }),
                      ),
                    disabled: onlyAddCodes,
                  },
                ]}
              />
            </div>
          </div>

          <hr className="m-form-separator" />

          <h2 className="add-product-subtitle">
            {translate(
              resources.products.add.offerDetails.giftCard.emails.title,
            )}
          </h2>

          <div className="m-form-section">
            <div className="m-form-label">
              <h4>
                {translate(
                  resources.products.add.offerDetails.giftCard.emails.label,
                )}
              </h4>
            </div>
            <div className="m-form-input">
              <TextArea
                value={offerDetailsForm.emails ?? ''}
                onChange={e =>
                  setOfferDetailsForm(
                    produce(offerDetailsForm, d => {
                      d.emails = e.value;
                    }),
                  )
                }
                errors={errors.emails}
                clearErrors={() => clearErrors('emails')}
                disabled={onlyAddCodes}
                rows={4}
              />
              <span className="m-form-info">
                {translate(
                  resources.products.add.offerDetails.giftCard.emails.info,
                )}
              </span>
            </div>
          </div>
        </div>
      </div>

      {buttonFunctions && (
        <div className="m-actions-panel">
          <Button
            onClick={buttonFunctions.onBackClick}
            className="btn btn-outline"
            disabled={isSaveButtonLoading}
          >
            <span className="btn-text">{translate(resources.back)}</span>
          </Button>
          <Button
            onClick={buttonFunctions.onContinueClick}
            className="btn btn-solid"
            loading={isSaveButtonLoading}
          >
            <span className="btn-text">
              {translate(resources.saveAndContinue)}
            </span>
          </Button>
        </div>
      )}

      {activeDialog === CodeDialogTypes.Generate && (
        <GeneratedCodesDialog
          setGeneratedCodes={codes => {
            setOfferDetailsForm(
              produce(offerDetailsForm, d => {
                d.codes.generated = codes;
                d.codes.manuallyAdded = { valid: [], invalid: [] };
                d.codesSource = CodeSources.Generated;
              }),
            );
          }}
          showPreviewDialog={() => setActiveDialog(CodeDialogTypes.Preview)}
          closeDialog={() => setActiveDialog(undefined)}
        />
      )}

      {activeDialog === CodeDialogTypes.ManuallyAdded && (
        <ManuallyAddedCodesDialog
          setCodes={codes =>
            setOfferDetailsForm(
              produce(offerDetailsForm, d => {
                d.codes.manuallyAdded = codes;
                d.codes.generated = [];
                d.codesSource = CodeSources.ManuallyAdded;
              }),
            )
          }
          closeDialog={() => setActiveDialog(undefined)}
          soCodeType={soCodeType}
        />
      )}

      {activeDialog === CodeDialogTypes.Preview && (
        <PreviewDialog
          codes={offerDetailsForm.codes.generated}
          closeDialog={() => setActiveDialog(undefined)}
          generateAgain={() => setActiveDialog(CodeDialogTypes.Generate)}
        />
      )}
    </>
  );
};
